import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Check, Briefcase, Users, DollarSign, Clock } from 'lucide-react';

const Home = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm fixed w-full z-10">
        <nav className="container mx-auto px-6 py-3">
          <div className="flex justify-between items-center">
            <div className="text-2xl font-bold text-indigo-600">fetchConsultant</div>
            <div className="hidden md:flex space-x-4">
              <Link to="/consultants" className="text-gray-600 hover:text-gray-900">For Consultants</Link>
              <Link to="/clients" className="text-gray-600 hover:text-gray-900">For Clients</Link>
              <a href="#how-it-works" className="text-gray-600 hover:text-gray-900">How It Works</a>
            </div>
            <div>
              <Link to="/app" className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded-full transition duration-300">
                Get Started
              </Link>
            </div>
          </div>
        </nav>
      </header>

      <main className="pt-16">
        <section className="bg-indigo-700 text-white py-20">
          <div className="container mx-auto px-6 text-center">
            <h1 className="text-5xl font-bold mb-4">Revolutionizing Epic Consulting</h1>
            <p className="text-xl mb-8">Direct connections. Transparent pricing. Maximized earnings.</p>
            <div className="flex justify-center space-x-4">
              <Link to="/consultants" className="bg-white text-indigo-700 font-bold py-3 px-8 rounded-full text-lg hover:bg-gray-100 transition duration-300">
                For Consultants
              </Link>
              <Link to="/clients" className="bg-transparent border-2 border-white text-white font-bold py-3 px-8 rounded-full text-lg hover:bg-white hover:text-indigo-700 transition duration-300">
                For Clients
              </Link>
            </div>
            <div className="mt-12">
              <ChevronDown className="mx-auto animate-bounce" size={32} />
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-6">
            <h2 className="text-3xl font-bold mb-8 text-center text-gray-800">Why Choose fetchConsultant?</h2>
            <div className="grid md:grid-cols-4 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <DollarSign className="text-indigo-600 mb-4" size={32} />
                <h3 className="text-xl font-semibold mb-2">Maximized Earnings</h3>
                <p className="text-gray-600">Keep more of what you earn with our flat 9% fee.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <Users className="text-indigo-600 mb-4" size={32} />
                <h3 className="text-xl font-semibold mb-2">Direct Connections</h3>
                <p className="text-gray-600">Connect directly with healthcare organizations or top consultants.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <Clock className="text-indigo-600 mb-4" size={32} />
                <h3 className="text-xl font-semibold mb-2">Rapid Deployment</h3>
                <p className="text-gray-600">From job posting to placement in days, not weeks.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <Check className="text-indigo-600 mb-4" size={32} />
                <h3 className="text-xl font-semibold mb-2">Quality Assured</h3>
                <p className="text-gray-600">Maintain high standards through peer and client reviews.</p>
              </div>
            </div>
          </div>
        </section>

        <section id="how-it-works" className="py-20 bg-gray-100">
          <div className="container mx-auto px-6">
            <h2 className="text-3xl font-bold mb-8 text-center text-gray-800">How It Works</h2>
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center mb-6">
                <div className="bg-indigo-600 rounded-full p-3 text-white mr-4">1</div>
                <p className="text-lg"><span className="font-semibold">Sign Up:</span> Create your account as a consultant or client.</p>
              </div>
              <div className="flex items-center mb-6">
                <div className="bg-indigo-600 rounded-full p-3 text-white mr-4">2</div>
                <p className="text-lg"><span className="font-semibold">Complete Your Profile:</span> Add your skills, experience, and preferences.</p>
              </div>
              <div className="flex items-center mb-6">
                <div className="bg-indigo-600 rounded-full p-3 text-white mr-4">3</div>
                <p className="text-lg"><span className="font-semibold">Connect:</span> Post or apply for opportunities that match your needs.</p>
              </div>
              <div className="flex items-center">
                <div className="bg-indigo-600 rounded-full p-3 text-white mr-4">4</div>
                <p className="text-lg"><span className="font-semibold">Collaborate:</span> Work together on exciting healthcare IT projects.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="container mx-auto px-6">
            <h2 className="text-3xl font-bold mb-8 text-center text-gray-800">Success Stories</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <p className="text-gray-600 mb-4">"fetchConsultant helped me increase my earnings by 20% while giving me more control over my career."</p>
                <p className="font-semibold">- Jane Doe, Epic Consultant</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <p className="text-gray-600 mb-4">"We reduced our hiring time by 50% and found high-quality consultants quickly and efficiently."</p>
                <p className="font-semibold">- John Smith, Healthcare IT Director</p>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-indigo-700 text-white py-20">
          <div className="container mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Transform Healthcare IT Staffing?</h2>
            <p className="text-xl mb-8">Join fetchConsultant today and experience the future of Epic consulting.</p>
            <Link to="/app" className="bg-white text-indigo-700 font-bold py-3 px-8 rounded-full text-lg hover:bg-gray-100 transition duration-300">
              Get Started Now
            </Link>
          </div>
        </section>
      </main>

      <footer className="bg-gray-800 text-white py-8">
        <div className="container mx-auto px-6">
          <div className="flex flex-wrap justify-between">
            <div className="w-full md:w-1/4 mb-6 md:mb-0">
              <h3 className="text-lg font-semibold mb-2">fetchConsultant</h3>
              <p className="text-sm">Revolutionizing Epic consulting connections.</p>
            </div>
            <div className="w-full md:w-1/4 mb-6 md:mb-0">
              <h3 className="text-lg font-semibold mb-2">Quick Links</h3>
              <ul className="text-sm">
                <li><Link to="/consultants" className="hover:text-indigo-400">For Consultants</Link></li>
                <li><Link to="/clients" className="hover:text-indigo-400">For Clients</Link></li>
                <li><a href="#how-it-works" className="hover:text-indigo-400">How It Works</a></li>
              </ul>
            </div>
            <div className="w-full md:w-1/4 mb-6 md:mb-0">
              <h3 className="text-lg font-semibold mb-2">Contact</h3>
              <p className="text-sm">Email: info@fetchconsultant.com</p>
            </div>
            <div className="w-full md:w-1/4">
              <h3 className="text-lg font-semibold mb-2">Follow Us</h3>
              <div className="flex space-x-4">
                <a href="https://x.com/fetchconsultant" target="_blank" rel="noopener noreferrer" className="hover:text-indigo-400">
                  <img src="/images/x-logo.png" alt="X logo" className="w-6 h-6" />
                </a>
                <a href="https://linkedin.com/company/fetchconsultant" target="_blank" rel="noopener noreferrer" className="hover:text-indigo-400">
                  <img src="/images/linkedin-logo.png" alt="LinkedIn logo" className="w-6 h-6" />
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-8 pt-8 text-sm text-center">
            <p>&copy; 2024 fetchConsultant. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;